/**
 * EditLockGuard — wraps an editable section. While the lock is held by someone
 * else, displays a banner and renders children as inert. While we hold it,
 * silently passes children through. If we LOSE a lock we previously held
 * (idle past TTL), shows a recovery modal so the user can copy any unsaved
 * changes before the form is replaced.
 *
 * Children receive the live `editLockSessionId` and a `notifyDirty` callback
 * via React context — `useEditLockSession()` is the consumer hook. Forms must
 * include `sessionId` on save calls (header `X-Edit-Lock-Session`) so the
 * server-side save guard can verify the lock is still held at write time.
 */

import React, { createContext, useContext, useRef, useState } from 'react';
import { useEditLock } from '../utils/useEditLock';
import { useTabPresence } from '../utils/useTabPresence';
import EditLockBanner from './EditLockBanner';
import LostLockModal from './LostLockModal';
import DuplicateTabModal from './DuplicateTabModal';
import IdleNudge from './IdleNudge';

interface LockSessionContext {
  sessionId: string;
  /** Called by children with their current dirty form snapshot — used by the lost-lock modal. */
  setDirtySnapshot: (snapshot: string | null) => void;
  /** Trigger an immediate heartbeat (used by the idle nudge). */
  ping: () => Promise<boolean>;
  /** Inform the guard that the server reported the lock is gone (e.g. 409 on save). */
  signalLockLost: () => void;
}

const LockSessionCtx = createContext<LockSessionContext | null>(null);

export function useEditLockSession(): LockSessionContext | null {
  return useContext(LockSessionCtx);
}

interface Props {
  resourceType: string;
  resourceId: string | number | null | undefined;
  enabled?: boolean;
  resourceLabel?: string;
  children: React.ReactNode;
}

const EditLockGuard: React.FC<Props> = ({ resourceType, resourceId, enabled = true, resourceLabel, children }) => {
  // Detect another tab on this device editing the same resource. Runs in
  // parallel with the lock acquire — if a sibling tab is found, we suppress
  // the acquire entirely so we don't dispossess them.
  const { otherTabPresent, isCheckingPresence } = useTabPresence(resourceType, resourceId, enabled);
  const [duplicateDismissed, setDuplicateDismissed] = useState(false);

  // Gate the lock acquire on presence resolution. Skipping while presence is
  // unresolved avoids a race where we briefly steal the lock from a sibling
  // tab during the 300ms ping window.
  const lockEnabled = enabled && !isCheckingPresence && !otherTabPresent;

  const { status, holder, sessionId, lostLock, error, retry, takeover, ping, acknowledgeLostLock, signalLockLost } =
    useEditLock(resourceType, resourceId, lockEnabled);
  const dirtySnapshotRef = useRef<string | null>(null);

  const setDirtySnapshot = (snap: string | null) => { dirtySnapshotRef.current = snap; };

  // Duplicate-tab path: show the modal until dismissed, then render children
  // inert (read-only) — same posture as a cross-user denial, but with no
  // banner / takeover button (this user already has the active edit elsewhere).
  if (otherTabPresent) {
    if (!duplicateDismissed) {
      return <DuplicateTabModal resourceLabel={resourceLabel} onDismiss={() => setDuplicateDismissed(true)} />;
    }
    return (
      <div
        // @ts-expect-error inert is valid HTML; React 18 DOM types may not list it
        inert=""
        className="opacity-60 pointer-events-none select-none"
        aria-hidden="true"
      >
        {children}
      </div>
    );
  }

  if (status === 'error') {
    // Don't lock the user out of editing on an infrastructure failure.
    console.warn('[EditLockGuard] lock service error, falling back to unguarded edit:', error);
    return <>{children}</>;
  }

  const ctx: LockSessionContext = { sessionId, setDirtySnapshot, ping, signalLockLost };

  if (status === 'denied') {
    return (
      <LockSessionCtx.Provider value={ctx}>
        <EditLockBanner holder={holder} onRefresh={retry} onTakeover={takeover} resourceLabel={resourceLabel} />
        <div
          // @ts-expect-error inert is valid HTML; React 18 DOM types may not list it
          inert=""
          className="opacity-60 pointer-events-none select-none"
          aria-hidden="true"
        >
          {children}
        </div>
      </LockSessionCtx.Provider>
    );
  }

  return (
    <LockSessionCtx.Provider value={ctx}>
      {/* Idle nudge only fires while we hold the lock. LostLockModal takes
          over the moment a heartbeat fails. */}
      <IdleNudge active={status === 'held' && !lostLock} ping={ping} />
      {lostLock && (
        <LostLockModal
          holder={holder}
          dirtySnapshot={dirtySnapshotRef.current}
          onAcknowledge={() => {
            acknowledgeLostLock();
            // Reload the page to pull whatever the next editor saved. The
            // modal's "copy your changes" affordance gives the user a chance
            // to capture their typed edits before this happens.
            window.location.reload();
          }}
        />
      )}
      {children}
    </LockSessionCtx.Provider>
  );
};

export default EditLockGuard;
